/**
 * Account reporting READ endpoints — request schemas + wire types.
 *
 * Plain RLS-scoped reads (like the clearing reads): the Hono handler runs the
 * query through the per-request user client, so org RLS does the authorization.
 * Routing through the backend keeps the accounting data-access layer in one
 * place. See docs/BACKEND_API.md → "Read endpoints".
 *
 * The wire types below are shared by the API handlers (response construction)
 * and the web hooks (`useAccountMetrics`, `useAccountTransactionsWithBalance`,
 * `useBankMetrics`, `useAccountBalances`) so the two sides cannot drift.
 */
import { z } from "zod";
/**
 * Calendar-date string (yyyy-MM-dd) — window bounds and "as of" cut-offs
 * (month-activity range, the daily-metrics `since`, the balances `asOfDate`).
 */
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: "Expected yyyy-MM-dd",
});
/**
 * Query params for GET /orgs/:orgId/accounts/:accountId/monthly-activity.
 * The window spans every period the metrics hook compares (prior-year start →
 * current-month end); the hook derives all comparisons + the trend client-side.
 */
export const accountMonthlyActivityQuerySchema = z.object({
    startDate: isoDateSchema,
    endDate: isoDateSchema,
});
/**
 * GET /orgs/:orgId/accounts/:accountId/register takes **no query params** —
 * the org and the account are path params, and the
 * `get_account_transactions_with_balance` RPC takes nothing else. There is
 * deliberately no schema for it; the handler has nothing to `safeParse`.
 */
/**
 * Query params for GET /orgs/:orgId/accounts/:accountId/daily-metrics.
 * `since` is the lower bound of the day window (the web hook passes
 * "one year ago" and derives the 12 month buckets + the 30-day window
 * client-side, so month boundaries stay on the *browser's* clock).
 */
export const bankDailyMetricsQuerySchema = z.object({
    since: isoDateSchema,
});
/**
 * Query params for GET /orgs/:orgId/account-balances.
 *
 * Everything arrives as a string on a GET, so `accountIds` is modelled as a
 * comma-separated list and transformed to `string[] | undefined` for the
 * handler. Absent / empty means "no account filter": the handler must then
 * omit `p_account_ids` entirely so the RPC's param DEFAULT applies — the same
 * `options?.accountIds?.length ? ... : undefined` semantics the web hook has
 * today (useLedgerTransactions.ts, `useAccountBalances`).
 */
export const accountFundBalancesQuerySchema = z.object({
    accountIds: z
        .string()
        .optional()
        .transform((v) => {
        const ids = (v ?? "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
        return ids.length > 0 ? ids : undefined;
    })
        .refine((ids) => ids === undefined ||
        ids.every((id) => z.string().uuid().safeParse(id).success), { message: "accountIds must be a comma-separated list of UUIDs" }),
    fundId: z.string().uuid().optional(),
    asOfDate: isoDateSchema.optional(),
});
//# sourceMappingURL=accounts.js.map